// Simple download utility that opens popup and uses html2canvas directly
import html2canvas from 'html2canvas';

export const downloadBadgeSimple = async (badgeUrl: string, filename: string = 'badge.png') => {
  return new Promise<void>((resolve, reject) => {
    // Open badge in a popup window
    const popup = window.open(
      badgeUrl, 
      '_blank', 
      'width=1200,height=800,scrollbars=no,resizable=yes'
    );
    
    if (!popup) {
      reject(new Error('Popup blocked'));
      return;
    }

    // Wait for popup to load
    popup.onload = () => {
      setTimeout(() => {
        try {
          // Add download functionality to the popup
          const script = popup.document.createElement('script');
          script.innerHTML = `
            // Add html-to-image to the popup
            const script = document.createElement('script');
            script.src = 'https://cdn.jsdelivr.net/npm/html-to-image@1.11.11/dist/html-to-image.min.js';
            document.head.appendChild(script);
            
            script.onload = () => {
              // Add Google Fonts and CSS reset for better text positioning
              const fontLink = document.createElement('link');
              fontLink.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap';
              fontLink.rel = 'stylesheet';
              document.head.appendChild(fontLink);
              
              const style = document.createElement('style');
              style.innerHTML = [
                "*, *::before, *::after { box-sizing: border-box; }",
                "h1, h2, h3, h4, h5, h6, p, span, div { margin: 0; padding: 0; line-height: inherit; vertical-align: baseline; font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif !important; }",
                "#badge-container { font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif !important; }",
                "#badge-container * { text-rendering: optimizeLegibility; -webkit-font-smoothing: antialiased; -moz-osx-font-smoothing: grayscale; font-family: inherit !important; }",
              ].join('\n');
              document.head.appendChild(style);

              // Helper to await font loading for consistent text metrics
              const waitForFonts = async () => {
                try {
                  if (document.fonts && document.fonts.ready) {
                    await document.fonts.ready;
                    // Explicitly load common sizes/weights used
                    const loads = [
                      '400 16px Inter',
                      '500 18px Inter',
                      '600 20px Inter',
                      '600 24px Inter',
                      '700 28px Inter',
                      '700 32px Inter',
                    ].map(desc => (document as any).fonts.load(desc));
                    await Promise.race([
                      Promise.all(loads),
                      new Promise(res => setTimeout(res, 1200)) // fallback timeout
                    ]);
                  } else {
                    // Fallback if Font Loading API unavailable
                    await new Promise(res => setTimeout(res, 800));
                  }
                } catch {}
              };
              
              // Add download button
              const downloadBtn = document.createElement('button');
              downloadBtn.innerHTML = 'Download Image';
              downloadBtn.style.cssText = \`
                position: fixed;
                top: 10px;
                right: 10px;
                z-index: 10000;
                background: #00529B;
                color: white;
                border: none;
                padding: 10px 15px;
                border-radius: 5px;
                cursor: pointer;
                font-family: Arial, sans-serif;
                font-size: 14px;
                box-shadow: 0 2px 10px rgba(0,0,0,0.2);
              \`;
              
              downloadBtn.onclick = async () => {
                try {
                  const container = document.getElementById('badge-container') || document.getElementById('banner-container');
                  if (!container) {
                    alert('Badge container not found');
                    return;
                  }
                  
                  downloadBtn.innerHTML = 'Processing images...';
                  downloadBtn.disabled = true;
                  
                  // Replace image URLs with CORS-enabled proxy URLs
                  const images = container.querySelectorAll('img');
                  const imagePromises = [];
                  
                  images.forEach((img) => {
                    if (!img.src || img.src.startsWith('data:')) return;

                    const backendHost = 'https://languageintelligence-backend.bartoszbudzynski.pl';
                    let targetSrc = img.src;

                    // Prefer proxy for backend static assets to ensure CORS headers
                    if (targetSrc.startsWith(backendHost) && targetSrc.includes('/static/')) {
                      const staticPath = targetSrc.split('/static/')[1];
                      if (staticPath) {
                        const proxyUrl = backendHost + '/public/image-proxy/' + staticPath;
                        if (proxyUrl !== targetSrc) {
                          console.log('Replacing image URL:', targetSrc, '→', proxyUrl);
                          targetSrc = proxyUrl;
                        }
                      }
                    }

                    const fetchToDataURL = (url) => fetch(url, { mode: 'cors', cache: 'no-store', credentials: 'omit' })
                      .then(r => { if (!r.ok) throw new Error('HTTP ' + r.status); return r.blob(); })
                      .then(blob => new Promise((res, rej) => { const fr = new FileReader(); fr.onloadend = () => res(fr.result); fr.onerror = rej; fr.readAsDataURL(blob); }));

                    const tryUrls = [];
                    if (targetSrc.startsWith(backendHost) && !targetSrc.includes('/public/image-proxy')) {
                      // Generic proxy by full URL param, then static-path proxy, then direct URL
                      tryUrls.push(backendHost + '/public/image-proxy?url=' + encodeURIComponent(targetSrc));
                      if (targetSrc.includes('/static/')) {
                        const sp = targetSrc.split('/static/')[1];
                        if (sp) tryUrls.push(backendHost + '/public/image-proxy/' + sp);
                      }
                    }
                    tryUrls.push(targetSrc);

                    const p = tryUrls.reduce((chain, url) => {
                      return chain.catch(() => fetchToDataURL(url));
                    }, Promise.reject(null))
                      .then((dataUrl) => { img.src = dataUrl as string; })
                      .catch(() => new Promise((resolve) => {
                        // Fallback: try reloading with crossOrigin + cache-bust
                        (img as any).crossOrigin = 'anonymous';
                        const sep = targetSrc.includes('?') ? '&' : '?';
                        img.src = targetSrc + sep + 'cb=' + Date.now();
                        img.onload = () => resolve(null);
                        img.onerror = () => resolve(null);
                      }));

                    imagePromises.push(p);
                  });
                  
                  // Wait for all images to load
                  await Promise.all(imagePromises);
                  
                  // Wait a bit for the DOM to update
                  await new Promise(resolve => setTimeout(resolve, 500));
                  
                  // Ensure web fonts are fully loaded before capture
                  downloadBtn.innerHTML = 'Loading fonts...';
                  await waitForFonts();

                  // Freeze computed text metrics to px to avoid baseline shifts
                  const textSelectors = 'h1,h2,h3,h4,h5,h6,p,span,div,li,a,button,small,label';
                  const textEls = container.querySelectorAll(textSelectors);
                  textEls.forEach((el) => {
                    const cs = window.getComputedStyle(el);
                    el.style.lineHeight = cs.lineHeight;
                    el.style.fontFamily = cs.fontFamily;
                    el.style.fontWeight = cs.fontWeight;
                    el.style.fontSize = cs.fontSize;
                    el.style.letterSpacing = cs.letterSpacing;
                  });

                  downloadBtn.innerHTML = 'Capturing...';
                  
                  const rect = (container as HTMLElement).getBoundingClientRect();
                  const maxDim = 3200; // cap the larger dimension to ~3200px
                  const scaleW = maxDim / Math.max(rect.width, 1);
                  const scaleH = maxDim / Math.max(rect.height, 1);
                  const pixelRatio = Math.min(4, Math.max(2, Math.min(scaleW, scaleH)));
                  const dataUrl = await window.htmlToImage.toPng(container, {
                    cacheBust: true,
                    backgroundColor: 'transparent',
                    pixelRatio,
                    quality: 1,
                    skipFonts: false,
                    useCors: true,
                    fetchRequestInit: { mode: 'cors', cache: 'no-store', credentials: 'omit' }
                  });

                  const a = document.createElement('a');
                  a.href = dataUrl;
                  a.download = '${filename}';
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  
                  downloadBtn.innerHTML = 'Downloaded!';
                  setTimeout(() => {
                    window.close();
                  }, 1000);
                  
                } catch (error) {
                  console.error('Download failed:', error);
                  downloadBtn.innerHTML = 'Error - Try right-click save';
                  downloadBtn.disabled = false;
                }
              };
              
              document.body.appendChild(downloadBtn);
              
              // Auto-click after a delay to allow images to load
              setTimeout(() => {
                downloadBtn.click();
              }, 3000);
            };
          `;
          popup.document.body.appendChild(script);
          // Resolve after setup
          setTimeout(() => {
            resolve();
          }, 500);
          
        } catch (error) {
          reject(error);
        }
      }, 2000);
    };

    popup.onerror = () => {
      reject(new Error('Failed to load popup'));
    };
  });
};
